import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import type { ConsentChoices } from '@/lib/consent/consent-signals';

import { Button } from '@/components/base/buttons/button';
import { Toggle } from '@/components/base/toggle/toggle';
import { CookieIcon } from '@/components/foundations/cookie-icon';
import { CONSENT_CATEGORY_COPY } from '@/lib/consent/consent-category-copy';
import {
  DENY_ALL_CHOICES,
  GRANT_ALL_CHOICES,
  OPTIONAL_CONSENT_CATEGORIES,
  isChoiceGranted,
} from '@/lib/consent/consent-signals';
import { useConsent } from '@/lib/consent/use-consent';
import { cx } from '@/utils/cx';

/**
 * Saving the manage panel is an answer to every category the visitor was
 * shown. Remaining `unset` values become `denied` so the write is explicit.
 */
function explicitChoices(choices: ConsentChoices): ConsentChoices {
  const next: ConsentChoices = { ...choices };
  for (const category of OPTIONAL_CONSENT_CATEGORIES) {
    if (next[category] === 'unset') next[category] = 'denied';
  }
  return next;
}

/**
 * Consent prompt. Visibility is entirely `ConsentProvider`'s decision — this
 * component renders whatever `promptMode` says and nothing else. In
 * particular it records a choice only when the visitor makes one: there is no
 * dismiss-without-answering path, because a dismissal is not consent.
 */
export function CookieBanner() {
  const { promptMode, answers, save } = useConsent();
  const [isManaging, setIsManaging] = useState(false);
  const [draft, setDraft] = useState<ConsentChoices>(answers);

  if (promptMode === 'hidden') return null;

  const isCompact = promptMode === 'compact';

  // Seed the panel from the visitor's own answers as they stand when it opens,
  // not from the mount-time state: consent resolves in an effect after the
  // first render, so the mount snapshot is the pre-resolution all-unset. The
  // version re-prompt also reaches users who consented under v1, and their
  // existing answers must not read as off in the panel they are about to save.
  //
  // Answers, specifically, and not the consent in force: a category the region
  // policy grants but the visitor never answered shows as off, because saving
  // this panel records a decision and the visitor has not made one about it.
  const toggleManaging = () => {
    if (!isManaging) setDraft(explicitChoices(answers));
    setIsManaging(!isManaging);
  };

  const toggleCategory = (category: keyof ConsentChoices) => {
    setDraft((previous) => ({
      ...previous,
      [category]: previous[category] === 'granted' ? 'denied' : 'granted',
    }));
  };

  return (
    <div
      data-testid="cookie-banner"
      className={cx(
        'fixed right-0 bottom-0 left-0 z-50 flex flex-col gap-3 border-t border-secondary bg-primary shadow-lg',
        isCompact ? 'px-3 py-2' : 'px-4 py-3 md:px-6',
      )}
    >
      <div
        className={cx(
          'flex flex-col gap-3',
          !isManaging && 'md:flex-row md:items-center md:gap-4',
        )}
      >
        {!isCompact && (
          <CookieIcon className="shrink-0 self-center md:self-auto" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-primary">
            <Trans>We value your privacy</Trans>
          </p>
          <p
            className={cx('text-secondary', isCompact ? 'text-xs' : 'text-sm')}
          >
            <Trans>
              We use cookies and similar technologies to run this site, measure
              how it performs, and — with your permission — to measure and
              personalize advertising. You can choose what to allow.
            </Trans>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-center gap-2 md:justify-start">
          <Button
            size="sm"
            color="secondary"
            onClick={() => save(DENY_ALL_CHOICES, 'banner')}
          >
            <Trans>Reject all</Trans>
          </Button>
          <Button size="sm" color="secondary" onClick={toggleManaging}>
            <Trans>Manage preferences</Trans>
          </Button>
          <Button
            size="sm"
            color="primary"
            onClick={() => save(GRANT_ALL_CHOICES, 'banner')}
          >
            <Trans>Accept all</Trans>
          </Button>
        </div>
      </div>

      {isManaging && (
        <div className="flex flex-col gap-3 border-t border-secondary pt-3">
          <Toggle
            size="sm"
            isSelected
            isDisabled
            onToggle={() => {}}
            label={CONSENT_CATEGORY_COPY.essential.label()}
            hint={CONSENT_CATEGORY_COPY.essential.hint()}
          />
          {OPTIONAL_CONSENT_CATEGORIES.map((category) => (
            <Toggle
              key={category}
              size="sm"
              isSelected={isChoiceGranted(draft[category])}
              onToggle={() => toggleCategory(category)}
              label={CONSENT_CATEGORY_COPY[category].label()}
              hint={CONSENT_CATEGORY_COPY[category].hint()}
            />
          ))}
          <div className="flex justify-end">
            <Button
              size="sm"
              color="primary"
              onClick={() => save(explicitChoices(draft), 'banner')}
            >
              <Trans>Save preferences</Trans>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
